import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UserManager, UserManagerSettings, User, StateStore } from "oidc-client";
import { audit, BehaviorSubject, defer, firstValueFrom, from, lastValueFrom, map, Observable, ReplaySubject, take, tap } from 'rxjs';
import { BaseService } from './base.service';
import { environment } from 'src/environments/environment';
import { ISAPI, ISPagination } from "is-lib";
import { ServerResponse } from '../models/ServerResponse';
import { GenericService } from './generic.service';

/**
 * Keeps the OIDC user in sessionStorage under a fixed key (environment.authStorageKey)
 * instead of oidc-client's default `oidc.user:<authority>:<clientId>`.
 * The user store only ever holds one user, so the key oidc-client passes in can be ignored.
 */
class FixedKeyUserStore implements StateStore {
  constructor(private key: string, private store: Storage = window.sessionStorage) { }

  set(_key: string, value: any): Promise<void> {
    this.store.setItem(this.key, value);
    return Promise.resolve();
  }

  get(_key: string): Promise<any> {
    return Promise.resolve(this.store.getItem(this.key));
  }

  remove(_key: string): Promise<any> {
    const value = this.store.getItem(this.key);
    this.store.removeItem(this.key);
    return Promise.resolve(value);
  }

  getAllKeys(): Promise<string[]> {
    return Promise.resolve(this.store.getItem(this.key) != null ? [this.key] : []);
  }
}

@Injectable({
  providedIn: 'root'
})
export class AuthService extends BaseService {
   // Observable navItem source
   private _authFlag = new ReplaySubject<User | null>(1);
   // Observable navItem stream
   authFlag$ = this._authFlag.asObservable();
   
   tokenIsExpiring = false;
   tokenIsExpiring$ = new BehaviorSubject(false);
   tokenExpired = false;

   public manager = new UserManager(this.getClientSettings());
   private oidc_user: User | null = null;

   managerPromise?: Promise<User | null>;

  constructor(
    private translateService: TranslateService,
    private _router: Router
  ) {
    super();
    this.manager.events.addAccessTokenExpiring( (eve: any) => { 
      this.tokenIsExpiring = true;
      this.tokenIsExpiring$.next(true)
      this.renewUserSignIn();
    });
    this.manager.events.addAccessTokenExpired( (eve: any) => { 
      this.tokenExpired = true;
      this.signout();
    });

    this.manager.events.addUserLoaded((user) => {
      this.oidc_user = user;
      this._authFlag.next(user);

      if (user != null) {
        this.tokenIsExpiring = false;
        this.tokenIsExpiring$.next(false);
        this.tokenExpired = false;
      }
    });

    // A failure reading the stored user (e.g. corrupted session data) is the same as having no session
    this.getUser().catch(() => this._authFlag.next(null));
  }

  async getUser(): Promise<User | null> {
    return this.manager.getUser().then( (val) => {
      if (val) {
        this.oidc_user = val;
      }

      this._authFlag.next(val);

      return val;
    });
  }

  login(returnUrl?: string) {
    return this.manager.signinRedirect({
      state: { returnUrl },
      extraQueryParams: { ui_locales: 'pt-PT' }
    });
  }

  /**
   * Tries to sign in using the existing IdentityServer session (e.g. from another tab) without showing the login form.
   * If there is no session, IdentityServer redirects back to the callback with a `login_required` error.
   */
  silentLogin(returnUrl?: string) {
    return this.manager.signinRedirect({
      prompt: 'none',
      state: { silent: true, returnUrl },
      extraQueryParams: { ui_locales: 'pt-PT' }
    });
  }

  /** Only allows relative app paths, so the return URL can't redirect to another site */
  static sanitizeReturnUrl(returnUrl?: string | null): string {
    return returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//') && !returnUrl.startsWith('/auth')
      ? returnUrl
      : '/';
  }

  async completeAuthentication(): Promise<User> {
    const user = await this.manager.signinRedirectCallback();
    this.oidc_user = user;
    this._authFlag.next(user);
    return user;
  }

  isAuthenticated(): Promise<boolean> {
    return firstValueFrom(this.authFlag$.pipe(take(1), map((user) => user != null && !user.expired) ));
  }

  // Several callers can ask for a sign-out at once (e.g. every request that finds the token expired); only the first one runs
  private _signingOut = false;

  async signout(redirectToLogin: boolean = true) {
    if (this._signingOut) {
      return;
    }

    this._signingOut = true;

    // Local data goes first: signoutRedirect navigates the page away, so anything after it may never run.
    // The OIDC user lives in sessionStorage, which signoutRedirect still reads, so clearing localStorage doesn't affect it.
    const uiLocale = this.translateService.getBrowserLang();
    localStorage.clear();
    this.clearCookies();

    try {
      await this.manager.signoutRedirect({
        extraQueryParams: { ui_locales: uiLocale }
      });
    } catch (error) {
      // IdentityServer unreachable: still drop the local session so the user isn't left half signed in
      try {
        await this.manager.removeUser();
      } catch { }

      this.oidc_user = null;
      this._authFlag.next(null);
      this._signingOut = false;

      await this._router.navigate(['/auth/login']);
    }
  }

  clearCookies() {
    var cookies = document.cookie.split(";"); // Obter todas as cookies
    for (var i = 0; i < cookies.length; i++) {
        var cookie = cookies[i];
        var eqPos = cookie.indexOf("=");
        var name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT"; // Definir a data de expiração no passado para excluir a cookie
    }
  }

  renewUserSignIn() {
    if (this.tokenIsExpiring) {
      this.manager.signinSilent()
    }
  }

  getClientSettings(): UserManagerSettings {
    return {
      authority: environment.authUrl,
      client_id: `${environment.appClientId}`,
      redirect_uri: `${environment.appUrl}/auth/auth-callback`,
      post_logout_redirect_uri: environment.appUrl,
      response_type: "code",
      scope: "openid profile is_clientportal_scope",
      loadUserInfo: true,
      filterProtocolClaims: true,
      userStore: new FixedKeyUserStore(environment.authStorageKey),
      automaticSilentRenew: false,
      accessTokenExpiringNotificationTime: 300, // 5 mins
      silent_redirect_uri: `${environment.appUrl}/silent-refresh.html`
    };
  }

  
  // getCountries(body?: ISAPI): Observable<ServerResponse<any>>{
  //   if (!body) {
  //     body = new ISAPI([], new ISPagination(999, 0));
  //   }

  //   return this._httpService.post(environment.apiUrl + 'Countries/Get', body, undefined, true)
  // }

  get authorizationHeaderValue(): string | null {
    if (this.oidc_user?.token_type && this.oidc_user?.access_token) {
      return `${this.oidc_user?.token_type} ${this.oidc_user?.access_token}`;
    }
    return null;
    
  }

  get userData(): any {
    return this.oidc_user;
  }

  get firstName(): string {
    return this.oidc_user?.profile.given_name ? this.oidc_user.profile.given_name : "";
  }

  get lastName(): string {
    return this.oidc_user?.profile.family_name ? this.oidc_user.profile.family_name : "";
  }

  get fullName(): string {
    return this.oidc_user?.profile.name ? this.oidc_user.profile.name : "";
  }

  get email(): string {
    return this.oidc_user?.profile.preferred_username ? this.oidc_user.profile.preferred_username : "";
  }
}